import { Injectable, Injector, inject } from '@angular/core';
import { Params } from '@angular/router';
import { AuthService } from '@abp/ng.core';
import { Observable, of } from 'rxjs';
import { CiamService } from './ciam.service';
import { StorageService } from './storage.service';

/**
 * Adaptateur du contrat `AuthService` d'ABP vers le CIAM, pour le site client uniquement.
 *
 * ABP fournit par défaut une implémentation de repli dont **chaque** méthode journalise
 * « You should add @abp/ng-oauth packages… » via `console.error`. Son initialiseur racine
 * (`getInitialData`) appelle `init()` à chaque chargement : sans cet adaptateur, toute page du
 * site client remonte une erreur au collecteur, alors que rien n'est en panne. Le site collègue
 * n'est pas concerné, il conserve `AbpOAuthModule`.
 *
 * Les méthodes non redéfinies restent celles d'ABP, et continueront donc de signaler leur appel :
 * c'est voulu. Si l'une d'elles est un jour sollicitée, c'est qu'un composant ABP attend une
 * authentification interne que ce site n'a pas, et il vaut mieux le voir.
 */
@Injectable()
export class CiamAbpAuthService extends AuthService {
  private readonly storageService = inject(StorageService);
  private readonly injector = inject(Injector);

  /** La session CIAM est restaurée par `CiamService`, pas au démarrage d'ABP. */
  override init(): Promise<void> {
    return Promise.resolve();
  }

  override get isInternalAuth(): boolean {
    return false;
  }

  override get isAuthenticated(): boolean {
    return this.storageService.hasValidAccessToken();
  }

  override getAccessToken(): string {
    return this.storageService.getAccessToken() ?? '';
  }

  override getRefreshToken(): string {
    return this.storageService.getRefreshToken() ?? '';
  }

  override getAccessTokenExpiration(): number {
    return this.storageService.getTokens()?.expiresAt ?? 0;
  }

  override navigateToLogin(queryParams?: Params): void {
    this.injector.get(CiamService).redirectToSignIn(queryParams?.['returnUrl']);
  }

  override login(): Observable<void> {
    this.navigateToLogin();
    return of(undefined);
  }

  override logout(): Observable<void> {
    return this.injector.get(CiamService).logout();
  }
}
